import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    GridColDef,
    GridPaginationModel,
    GridRenderCellParams,
    GridSortModel,
} from "@mui/x-data-grid-premium";
import { TransportOrderNotificationListItemDto, TransportOrderNotificationState } from "../../models/transport/transportOrderNotification";
import { getDateTime } from "../transport/components/transportUtils";
import { MuiDataGrid } from "../framework/muiDataGrid";
import { transportOrderStateNames } from "../../models/transport/transportOrder";
import { CustomPagination } from "../framework/editableDataGrid/customPagination";
import { useAppSelector } from "../../framework/customStore";
import { confirmAllMobileToDesktopNotifications, confirmTransportOrderNotification } from "../../services/transportOrderService";
import type { ApiResponse } from "../../services/apiClient";
import { showApiError } from "../framework/formUtils";
import { PagedResult } from "../../models/pagedResult/pagedResult";
import { Base } from "../../framework/base";
import { Box, Button, Typography } from "@mui/material";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";

interface NotificationsTableProps {
    notifications: TransportOrderNotificationListItemDto[];
    currentPage: number;
    pageSize: number;
    totalCount: number;
    onPageChange: (page: number) => void;
    onPageSizeChange: (pageSize: number) => void;
    fetchNotifications: (sortField?: string, sortOrder?: string) =>
        Promise<PagedResult<TransportOrderNotificationListItemDto>>;
}

export const NotificationsTable = ({
    notifications,
    currentPage,
    pageSize,
    totalCount,
    onPageChange,
    onPageSizeChange,
    fetchNotifications,
}: NotificationsTableProps) => {
    const { t } = useTranslation();

    const confirmTransportOrderNotificationSafe: (
        id: string
    ) => Promise<ApiResponse<unknown>> = confirmTransportOrderNotification;

    const [confirmDisabled, setConfirmDisabled] = useState(false);
    const [acknowledgingIds, setAcknowledgingIds] = useState<Set<string>>(new Set());
    const employees = useAppSelector((state) => state.employees.employees);

    // Convert our pagination to MUI DataGrid format
    const paginationModel = useMemo(() => ({
        page: currentPage - 1, // MUI uses 0-based indexing
        pageSize: pageSize
    }), [currentPage, pageSize]);

    const handlePaginationModelChange = useCallback((model: GridPaginationModel) => {
        onPageChange(model.page + 1); // Convert back to 1-based indexing
        onPageSizeChange(model.pageSize);
    }, [onPageChange, onPageSizeChange]);

    const handleConfirm = async() => {
        setConfirmDisabled(true);
        try {
            await confirmAllMobileToDesktopNotifications();
            fetchNotifications()
                .catch(() => {
                    showApiError(t("notifications.fetchFailed"));
                })
                .finally(() => {
                    setConfirmDisabled(false);
                });
        } catch (error) {
            setConfirmDisabled(false);
            console.error("Error confirming notifications!:", error);
        }
    };

    const handleAcknowledge = async(notificationId: string) => {
        setAcknowledgingIds((prev) => {
            const next = new Set(prev);
            next.add(notificationId);
            return next;
        });

        try {
            await confirmTransportOrderNotificationSafe(notificationId);
            await fetchNotifications();
        } catch (error) {
            console.error("Error acknowledging notification:", error);
            showApiError(t("notifications.acknowledgeFailed"));
        } finally {
            setAcknowledgingIds((prev) => {
                const next = new Set(prev);
                next.delete(notificationId);
                return next;
            });
        }
    };

    const handleSortModelChange = React.useCallback((sortModel: GridSortModel) => {
        if (sortModel[0]) {
            fetchNotifications(
                Base.capitalizeFirstChar(sortModel[0].field),
                sortModel[0].sort
            )
                .catch(() => {
                    showApiError(t("notifications.fetchFailed"));
                });
        } else {
            fetchNotifications()
                .catch(() => {
                    showApiError(t("notifications.fetchFailed"));
                });
        }
    }, []);

    const getStateText = (state: number) => {
        if (state < TransportOrderNotificationState.Confirmed.valueOf()) {
            return t("notifications.unconfirmed");
        }
        if (state === TransportOrderNotificationState.Confirmed.valueOf()) {
            return t("notifications.confirmed");
        }
        return "-";
    };

    const getEmployeeName = (id: string) => {
        const employee = employees.find(i => i.id === id);
        if (employee) {
            return employee.fullName;
        }

        return "-";
    };

    const gridDef: GridColDef<TransportOrderNotificationListItemDto>[] = useMemo(
        () => [
            {
                headerName: t("common.created"),
                field: "createdDateTime",
                width: 150,
                renderCell: (
                    params: GridRenderCellParams<TransportOrderNotificationListItemDto>
                ) => {
                    return (
                        <div>
                            {getDateTime(params.row?.createdDateTime)}
                        </div>
                    );
                },
            },
            {
                headerName: t("transport.order.creator"),
                field: "createdEmployeeId",
                sortable: false,
                type: "string",
                flex: 1,
                renderCell: (
                    params: GridRenderCellParams<TransportOrderNotificationListItemDto>
                ) => {
                    return (
                        <div>
                            {getEmployeeName(params.row?.createdEmployeeId)}
                        </div>
                    );
                },
            },
            {
                headerName: t("common.message"),
                field: "message",
                sortable: false,
                type: "string",
                flex: 4,
                cellClassName: "wrapCell",
                renderCell: (
                    params: GridRenderCellParams<TransportOrderNotificationListItemDto>
                ) => {
                    const driverComment =
                        typeof params.row?.driverComment === "string"
                            ? String(params.row.driverComment)
                            : "";

                    const orderStateKey =
                        params.row?.orderState !== undefined &&
                        params.row?.orderState !== null
                            ? (transportOrderStateNames[
                                params.row.orderState
                            ] as unknown as string | undefined)
                            : undefined;

                    return (
                        <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
                            <Typography variant="body2">
                                {t(params.row?.message, {
                                    orderNro: params.row?.transportOrderNumber,
                                    vehicleName: params.row?.vehicleRegisterNumber,
                                    state: orderStateKey ? t(orderStateKey) : "-",
                                    employeeName: getEmployeeName(params.row?.createdEmployeeId),
                                })}
                            </Typography>

                            {driverComment.trim().length > 0 && (
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "flex-start",
                                        gap: 0.75,
                                        mt: 0.5,
                                    }}
                                >
                                    <ChatBubbleOutlineIcon
                                        sx={{ fontSize: 16, mt: "2px", color: "text.secondary" }}
                                    />
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ whiteSpace: "pre-wrap" }}
                                    >
                                        {driverComment}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    );
                },
            },
            {
                headerName: t("common.status"),
                field: "state",
                sortable: false,
                type: "string",
                flex: 1,
                renderCell: (
                    params: GridRenderCellParams<TransportOrderNotificationListItemDto>
                ) => {
                    return (
                        <div>
                            {getStateText(params.row?.state)}
                        </div>
                    );
                },
            },
            {
                headerName: "",
                field: "acknowledge",
                sortable: false,
                filterable: false,
                width: 140,
                align: "right",
                headerAlign: "right",
                renderCell: (
                    params: GridRenderCellParams<TransportOrderNotificationListItemDto>
                ) => {
                    const id = params.row?.id;
                    const isAcknowledging = id ? acknowledgingIds.has(id) : false;

                    return (
                        <Button
                            size="small"
                            variant="contained"
                            color="success"
                            disabled={!id || isAcknowledging}
                            onClick={() => {
                                if (id) {
                                    void handleAcknowledge(id);
                                }
                            }}
                        >
                            {t("notifications.acknowledge")}
                        </Button>
                    );
                },
            },
        ],
        [employees]
    );

    return (
        <>
            <MuiDataGrid
                rows={notifications}
                columns={gridDef}
                density="compact"
                rowSelection={false}
                disableColumnFilter
                getRowHeight={() => "auto"}
                getEstimatedRowHeight={() => 56}
                pagination={true}
                paginationMode="server"
                paginationModel={paginationModel}
                onPaginationModelChange={handlePaginationModelChange}
                rowCount={totalCount}
                pageSizeOptions={[25, 50, 100]}
                sortingMode="server"
                onSortModelChange={handleSortModelChange}
                customPagination={notifications?.length > 0 ? CustomPagination : null}
                sx={{
                    "& .MuiDataGrid-cell": {
                        alignItems: "flex-start",
                        py: 1,
                    },
                    "& .MuiDataGrid-cell.wrapCell": {
                        whiteSpace: "normal",
                        lineHeight: 1.35,
                        overflowWrap: "anywhere",
                        wordBreak: "break-word",
                    },
                }}
                toolbarProps={{
                    title: t("notifications.masterList.tableHeader"),
                    enableColumnSelector: false,
                    disableQuickFilter: true,
                    disableToolbarColumnsButton: true,
                    customToolbarButtons: notifications?.length > 0 ? [
                        {
                            key: "confirm-all",
                            color: "success",
                            variant: "contained",
                            onClick: handleConfirm,
                            children: t("notifications.confirmAll"),
                            disabled: confirmDisabled,
                            sx: { marginRight: 1 }
                        },
                    ] : null
                }}
            />
        </>
    );
};
